/**
 * Lambda Microservices Feature Flag Configuration
 * 
 * This file controls routing between monolithic Lambda functions and microservices.
 * Matches the pattern used in the React Native app.
 */

// Feature flag to switch between monolith and microservices
const USE_MICROSERVICES = import.meta.env.VITE_USE_MICROSERVICES === 'true' || false;

// Log architecture mode on startup (only in development)
if (import.meta.env.DEV) {
  if (USE_MICROSERVICES) {
    console.log('🚀 [MICROSERVICES] Using microservices architecture');
  } else {
    console.log('🏛️  [MONOLITH] Using monolith architecture');
  }
}

// Microservice URLs - DEV
const MICROSERVICE_URLS_DEV = {
  stories: 'https://ls3efthao7x26vqizkvmogg5de0fjswd.lambda-url.eu-west-2.on.aws/',
  profiles: 'https://5k4s2iowd4jmsk7a24i6a2aaqu0usnjk.lambda-url.eu-west-2.on.aws/',
  teachers: 'https://gymagbicx4zrj6het2fn6aoobe0vrocm.lambda-url.eu-west-2.on.aws/',
  students: 'https://lx5tpdoajravmv6aookc2t4mka0qxgjs.lambda-url.eu-west-2.on.aws/',
  classes: 'https://6t4qxxdaayndigibdhciirmxry0wjgyu.lambda-url.eu-west-2.on.aws/',
  wordlists: 'https://d6nrppw7tvehhfi2h4sejyfho40nbyhj.lambda-url.eu-west-2.on.aws/',
  parents: 'https://bsu2qgicsfx4diemo7s2ldzkhy0bnwhe.lambda-url.eu-west-2.on.aws/',
  storyGeneration: 'https://leib6cqabohpnrkrqbesuivzcq0halzx.lambda-url.eu-west-2.on.aws/',
};

// Microservice URLs - PROD
const MICROSERVICE_URLS_PROD = {
  stories: 'https://fiomg6dtboyyexe7dzxbulznna0kcmaf.lambda-url.eu-west-2.on.aws/',
  profiles: 'https://werbtx6674cmrexnwhauaca3gy0zfyus.lambda-url.eu-west-2.on.aws/',
  teachers: 'https://3crhoyql62542l63krr4nvmzni0qbapu.lambda-url.eu-west-2.on.aws/',
  students: 'https://iwv6lpwz7ergxvqarvcqoemgke0mnsvx.lambda-url.eu-west-2.on.aws/',
  classes: 'https://2hb6jcbzsdo6emccpqa6gjajgy0prfdr.lambda-url.eu-west-2.on.aws/',
  wordlists: 'https://2i432tht2wta6aheomnxuxl6fy0lvlah.lambda-url.eu-west-2.on.aws/',
  parents: 'https://fxaddxn36yiobucxjkkfaneqc40cemfj.lambda-url.eu-west-2.on.aws/',
  storyGeneration: 'https://gzfztmljpuy7khsj5jooudqqsi0zenzu.lambda-url.eu-west-2.on.aws/', // Uses monolith for now
};

// Select microservice URLs based on environment
const IS_PRODUCTION = import.meta.env.VITE_ENVIRONMENT === 'production';
const MICROSERVICE_URLS = IS_PRODUCTION ? MICROSERVICE_URLS_PROD : MICROSERVICE_URLS_DEV;

// Monolith URLs (current production setup)
const MONOLITH_URLS = {
  main: import.meta.env.VITE_LAMBDA_URL || 'https://gzfztmljpuy7khsj5jooudqqsi0zenzu.lambda-url.eu-west-2.on.aws',
  education: import.meta.env.VITE_EDUCATION_LAMBDA_URL || 'https://pzo2glzq3tbd2qrkwx5jl6pyeu0dazjn.lambda-url.eu-west-2.on.aws',
};

export type HandlerType = 
  | 'auth' 
  | 'stories' 
  | 'profiles' 
  | 'teachers' 
  | 'students' 
  | 'classes' 
  | 'wordlists' 
  | 'parents'
  | 'storyGeneration'
  | 'licensing';

/**
 * Get Lambda URL based on handler type
 */
export const getLambdaUrl = (handlerType: HandlerType): string => {
  const isDev = import.meta.env.DEV;
  
  if (!USE_MICROSERVICES) {
    // Use monolith (production-safe default)
    // Education handlers: teachers, students, classes, wordlists, parents
    if (['teachers', 'students', 'classes', 'wordlists', 'parents'].includes(handlerType)) {
      if (isDev) console.log(`🏛️  [${handlerType.toUpperCase()}] → Education Monolith`);
      return MONOLITH_URLS.education;
    }
    // Standard handlers: auth, stories, profiles
    if (isDev) console.log(`🏛️  [${handlerType.toUpperCase()}] → Main Monolith`);
    return MONOLITH_URLS.main;
  }
  
  // Use microservices (testing only)
  const microserviceUrl = MICROSERVICE_URLS[handlerType as keyof typeof MICROSERVICE_URLS];
  
  if (!microserviceUrl || microserviceUrl.includes('[')) {
    console.warn(`⚠️  [${handlerType.toUpperCase()}] Microservice not configured, falling back to monolith`);
    // Fallback to monolith if microservice URL not configured
    if (['teachers', 'students', 'classes', 'wordlists', 'parents'].includes(handlerType)) {
      return MONOLITH_URLS.education;
    }
    return MONOLITH_URLS.main;
  }
  
  if (isDev) console.log(`🚀 [${handlerType.toUpperCase()}] → Microservice`);
  return microserviceUrl;
};

/**
 * Check if microservices are enabled
 */
export const isMicroservicesEnabled = (): boolean => USE_MICROSERVICES;

/**
 * Get all configured microservice URLs
 */
export const getMicroserviceUrls = () => ({ ...MICROSERVICE_URLS });

/**
 * Get all monolith URLs
 */
export const getMonolithUrls = () => ({ ...MONOLITH_URLS });
